<!-- @component
  Create/Edit modal for a QuantityUnit (Mengeneinheit).
  Opens via svelte-modals; validates with yup schema before
  calling createQuantity or updateQuantity.
-->
<script lang="ts">
	/* https://svelte-modals.mattjennings.io/api */
	import { modals } from 'svelte-modals'
	import Button from '$lib/atoms/Button.svelte'
	import { t } from '$i18n/i18n'
	import { fly } from 'svelte/transition'
	import IconCheckmark from '$svg/icon-checkmark.svg?component'
	import IconCross from '$svg/icon-cross.svg?component'
	import IconInfo from '$svg/icon-info.svg?component'
	import InputField from '$lib/molecules/InputField.svelte'
	import type { QuantityUnit } from './types/quantity'
	import { DefaultQuantityUnit, quantityUnitSchema } from './types/quantity'
	import type { FontProps } from '$types/fontProps'
	import { DefaultInputFieldProps } from '$types/fontProps'
	import { getFormErrors } from '$utils/formValidation'
	import { writable, type Writable } from 'svelte/store'
	import { getContext, hasContext, onMount, setContext } from 'svelte'
	import { updateQuantity } from './mmpv-quantities.controller.svelte'
	import QuantityMaterialsSection from './QuantityMaterialsSection.svelte'
	import Notifications from '$lib/organisms/Notifications.svelte'

	setContext('useMaterialDesign', true)

	const fontProps: FontProps = DefaultInputFieldProps()
	const headerFontProps: FontProps = DefaultInputFieldProps('13px', 500)

	const QUANTITY_FORM_ERROR_PATH_PREFIX = 'quantity.'

	interface Props {
		isOpen: boolean
		showCloseIcon?: boolean
		quantityUnit?: QuantityUnit
		isNew?: boolean
	}

	let {
		isOpen,
		showCloseIcon = false,
		quantityUnit = $bindable(DefaultQuantityUnit()),
		isNew = true
	}: Props = $props()

	const formErrors: Writable<{ path: string; message: string }[]> = hasContext('formErrors')
		? getContext('formErrors')
		: writable([])

	let quantityUnitCopy: QuantityUnit = DefaultQuantityUnit()
	let showOverlay: boolean = $state(false)

	const overlayTitle: string = $t('UI.mmpv.materialPropertyModal.unsavedChanges.title')
	const overlayMessage: string = $t('UI.mmpv.catalogModal.unsavedChanges.message')
	const overlayButtonLabel: string = $t('UI.mmpv.materialPropertyModal.unsavedChanges.button.quit')

	let overlayAction: () => void = $state(() => {})

	onMount(() => {
		quantityUnitCopy = structuredClone($state.snapshot(quantityUnit))
	})

	async function save(closeAfter = true) {
		$formErrors = $formErrors.filter((e) => !e.path.startsWith(QUANTITY_FORM_ERROR_PATH_PREFIX))
		try {
			const validated = await quantityUnitSchema.validate(quantityUnit, {
				stripUnknown: false,
				abortEarly: false
			})
			const payload = { ...quantityUnit, ...validated } as QuantityUnit
			const saved = await updateQuantity(payload)
			if (saved) {
				quantityUnit = { ...quantityUnit, ...saved }
				quantityUnitCopy = structuredClone($state.snapshot(quantityUnit))
				if (closeAfter) {
					modals.close()
				}
			}
		} catch (error) {
			$formErrors = [...$formErrors, ...getFormErrors(error, QUANTITY_FORM_ERROR_PATH_PREFIX)]
		}
	}

	function handleAbort() {
		const isUnedited =
			JSON.stringify($state.snapshot(quantityUnit)) === JSON.stringify(quantityUnitCopy)
		if (isUnedited) {
			modals.close()
		} else {
			showOverlay = true
			overlayAction = () => {
				quantityUnit = structuredClone(quantityUnitCopy)
				modals.close()
			}
		}
	}

	const isSaved = $derived(quantityUnit.publicId !== 'new')
</script>

{#if isOpen}
	<div
		role="dialog"
		class="modal"
		in:fly|global={{ y: -100, duration: 600 }}
		out:fly|global={{ y: -1000, duration: 600 }}
	>
		<div class="contents" style="--content-padding: 3rem 3.5rem; max-width: 920px; width: 920px">
			<Notifications target="modal" position="absolute" top="0" />
			{#if showCloseIcon}
				<Button
					variant="secondary-icon"
					class="cancel-btn"
					aria-label="Cancel"
					onclick={handleAbort}
				>
					<IconCross width={24} height={24} />
				</Button>
			{/if}
			{#if showOverlay}
				<div class="modal-overlay-content">
					<div>
						<span>{overlayTitle}</span>
						<p>{overlayMessage}</p>
					</div>
					<div>
						<Button variant="secondary" color="red" onclick={overlayAction}>
							{overlayButtonLabel}
						</Button>
						<Button
							variant="primary"
							color="red"
							onclick={() => {
								showOverlay = false
							}}
						>
							{$t('UI.mmpv.materialPropertyModal.button.continue')}
						</Button>
					</div>
				</div>
			{:else}
				<div class="actual-content">
					<h2>
						{isNew
							? $t('UI.mmpv.quantities.modal.title.new')
							: $t('UI.mmpv.quantities.modal.title.edit', { name: quantityUnit.name })}
					</h2>
					<p class="modal-subtitle">
						{isNew
							? $t('UI.mmpv.quantities.modal.subtitle.new')
							: $t('UI.mmpv.quantities.modal.subtitle.edit')}
					</p>

					<!-- Row 1: Name + Label -->
					<div class="field-row">
						<InputField
							bind:value={quantityUnit.name}
							id="input-quantity-name"
							label={$t('UI.mmpv.quantities.name')}
							placeholder={$t('UI.mmpv.quantities.name.ph')}
							{fontProps}
							{headerFontProps}
							errorPath={QUANTITY_FORM_ERROR_PATH_PREFIX + 'name'}
							required
						/>
						<InputField
							bind:value={quantityUnit.label}
							id="input-quantity-label"
							label={$t('UI.mmpv.quantities.label')}
							placeholder={$t('UI.mmpv.quantities.label.ph')}
							{fontProps}
							{headerFontProps}
							errorPath={QUANTITY_FORM_ERROR_PATH_PREFIX + 'label'}
							required
						/>
					</div>

					<!-- Row 2: Kurzlabel + Einheit -->
					<div class="field-row">
						<InputField
							bind:value={quantityUnit.shortLabel}
							id="input-quantity-shortlabel"
							label={$t('UI.mmpv.quantities.shortLabel')}
							placeholder={$t('UI.mmpv.quantities.shortLabel.ph')}
							{fontProps}
							{headerFontProps}
							errorPath={QUANTITY_FORM_ERROR_PATH_PREFIX + 'shortLabel'}
						/>
						<InputField
							bind:value={quantityUnit.unit}
							id="input-quantity-unit"
							label={$t('UI.mmpv.quantities.unit')}
							placeholder={$t('UI.mmpv.quantities.unit.ph')}
							{fontProps}
							{headerFontProps}
							errorPath={QUANTITY_FORM_ERROR_PATH_PREFIX + 'unit'}
							required
						/>
					</div>

					<!-- Not-saved-yet hint -->
					{#if isNew && !isSaved}
						<div class="not-saved-hint">
							<IconInfo width="16" height="16" fill="var(--color-blue-700)" />
							{$t('UI.mmpv.quantities.notSaved')}
						</div>
					{/if}

					<!-- Materials section (only when saved) -->
					{#if isSaved}
						<QuantityMaterialsSection
							quantityPublicId={quantityUnit.publicId}
							bind:materialNodeIds={quantityUnit.materialNodeIds}
						/>
					{/if}
				</div>
				<div class="actions">
					<Button variant="secondary" color="grey" onclick={handleAbort}>
						{$t('UI.button.cancel')}
					</Button>
					{#if isSaved}
						<Button variant="secondary" color="green" onclick={() => save(false)}>
							{$t('UI.button.save')}
						</Button>
					{/if}
					<Button
						id="btn-quantity-modal-save"
						variant="primary"
						color="green"
						onclick={() => save(isSaved)}
					>
						{!isSaved ? $t('UI.mmpv.quantities.modal.button') : $t('UI.button.saveAndClose')}
						<IconCheckmark width={20} height={20} />
					</Button>
				</div>
			{/if}
		</div>
	</div>
{/if}

<style lang="scss">
	@import '$styles/modals.scss';

	.actual-content {
		width: 100%;
		display: flex;
		flex-direction: column;
		align-items: flex-start;
		gap: 12px;
	}

	h2 {
		width: auto;
		margin: 0;
	}

	.modal-subtitle {
		width: 100%;
		margin: 0 0 8px;
		font-size: 12.5px;
		color: #000;
	}

	.field-row {
		display: flex;
		flex-direction: row;
		gap: 12px;
		width: 100%;
	}

	.actions {
		display: flex;
		flex-direction: row;
		justify-content: flex-end;
		align-items: center;
		gap: 6px;
		width: 100%;
		margin: 0;
		border-radius: 0 0 6px 6px;
	}

	.not-saved-hint {
		display: flex;
		align-items: center;
		gap: 6px;
		width: 100%;
		padding: 0.5rem 0.75rem;
		background: var(--color-blue-50);
		border: 1px solid var(--color-blue-200);
		border-radius: 4px;
		font-size: 13px;
		color: var(--color-blue-700);
	}
</style>
